import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Tabs,
  Tab,
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon, Home as HomeIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";

interface ClientPhoto {
  id: number;
  client_id: number;
  photo_type: "reference" | "after";
  file_path: string;
  created_at: string;
}

const API_BASE = process.env.REACT_APP_API_URL || "/api";
const UPLOADS_BASE = process.env.REACT_APP_UPLOADS_URL || "/uploads";

export default function MyPhotosPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<"reference" | "after">("reference");
  const [photos, setPhotos] = useState<ClientPhoto[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerPhoto, setViewerPhoto] = useState<ClientPhoto | null>(null);

  const openViewer = (photo: ClientPhoto) => {
    setViewerPhoto(photo);
    setViewerOpen(true);
  };

  const closeViewer = () => {
    setViewerOpen(false);
    setViewerPhoto(null);
  };

  const getAuthHeaders = (): Record<string, string> => {
    const t = localStorage.getItem("token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const load = async (type: "reference" | "after") => {
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE}/client-photos/my?photo_type=${type}`, {
        headers: getAuthHeaders(),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => null);
        throw new Error(err?.detail || "Не удалось загрузить фото");
      }
      const data = await resp.json();
      setPhotos(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn(e);
      alert(e instanceof Error ? e.message : "Ошибка загрузки фото");
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(tab);
  }, [tab]);

  const upload = async (file: File, type: "reference" | "after") => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await fetch(`${API_BASE}/client-photos/my/upload?photo_type=${type}`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: fd,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => null);
        throw new Error(err?.detail || "Не удалось загрузить фото");
      }
      await load(type);
    } catch (e) {
      console.warn(e);
      alert(e instanceof Error ? e.message : "Ошибка загрузки фото");
    } finally {
      setUploading(false);
    }
  };

  const del = async (photoId: number) => {
    try {
      const resp = await fetch(`${API_BASE}/client-photos/${photoId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => null);
        throw new Error(err?.detail || "Не удалось удалить фото");
      }
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
    } catch (e) {
      console.warn(e);
      alert(e instanceof Error ? e.message : "Ошибка удаления");
    }
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      <Header title="Мои фото" />

      <Box sx={{ p: 3 }}>
        <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Референсы и фото после
            </Typography>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
              <Tab label="Референсы" value="reference" />
              <Tab label="Фото после" value="after" />
            </Tabs>

            <Box display="flex" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
              <Button variant="contained" component="label" disabled={uploading}>
                Загрузить фото
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void upload(f, tab);
                    e.currentTarget.value = "";
                  }}
                />
              </Button>
              {loading ? (
                <Typography variant="body2" color="textSecondary" sx={{ alignSelf: "center" }}>
                  Загрузка...
                </Typography>
              ) : null}
            </Box>

            {photos.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                Фото пока нет
              </Typography>
            ) : (
              <Box
                display="grid"
                gridTemplateColumns={{ xs: "1fr 1fr", sm: "1fr 1fr 1fr", md: "1fr 1fr 1fr 1fr" }}
                gap={2}
              >
                {photos.map((p) => (
                  <Box key={p.id} sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                    <Box
                      component="img"
                      src={`${UPLOADS_BASE}/${p.file_path}`}
                      onClick={() => openViewer(p)}
                      sx={{
                        width: "100%",
                        height: 160,
                        objectFit: "cover",
                        display: "block",
                        cursor: "pointer",
                      }}
                    />
                    <Box display="flex" justifyContent="flex-end" px={1} py={0.5}>
                      <Button size="small" color="error" onClick={() => void del(p.id)}>
                        Удалить
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      <Dialog fullScreen open={viewerOpen} onClose={closeViewer}>
        <AppBar position="sticky" color="default" elevation={0}>
          <Toolbar>
            <IconButton edge="start" onClick={closeViewer} aria-label="Закрыть">
              <CloseIcon />
            </IconButton>
            <Box sx={{ flexGrow: 1 }} />
            <IconButton edge="end" onClick={() => navigate("/")} aria-label="На главную">
              <HomeIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box
          sx={{
            bgcolor: "background.default",
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: { xs: 1, sm: 2 },
          }}
        >
          {viewerPhoto ? (
            <Box
              component="img"
              src={`${UPLOADS_BASE}/${viewerPhoto.file_path}`}
              sx={{
                maxWidth: "100%",
                maxHeight: "calc(100vh - 64px)",
                objectFit: "contain",
                borderRadius: 2,
              }}
            />
          ) : null}
        </Box>
      </Dialog>
    </Box>
  );
}
